import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Target, Coins, TrendingUp } from 'lucide-react';
import { formatters } from '../../../../styles/tokens';
import { computeCertificateMetrics } from '../../../../services/metrics/certificatesMetrics';

function CertificatesMetricsCards({ data = [], options = {}, mode = 'full' }) {
  const metrics = computeCertificateMetrics(data, options);
  const { summary, porCompania, period } = metrics;

  const fmtUF = (v) => formatters?.formatUF ? formatters.formatUF(v) : `UF ${formatters?.formatNumber ? formatters.formatNumber(v) : Number(v).toFixed(2)}`;
  const fmtCLP = (v) => formatters?.formatCLP ? formatters.formatCLP(v) : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v);

  const hasRate = typeof options?.ufRateProvider === 'function' || (typeof options?.ufRate === 'number' && options.ufRate > 0);

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-600" />
            {mode === 'mrr' ? 'MRR · Suscripciones vigentes' : mode === 'arr' ? 'ARR · 12 × MRR' : `Métricas de Certificados ${period?.year && period?.month ? `· ${period.year}-${String(period.month).padStart(2, '0')}` : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mode === 'mrr' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">MRR (suscripciones vigentes)</div>
                  <div className="mt-1 text-lg font-semibold text-green-700">{fmtUF(summary?.mrrUF ?? 0)}</div>
                  {hasRate && <div className="text-sm text-muted-foreground">{fmtCLP(summary?.mrrCLP ?? 0)}</div>}
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Prima Total (recaudada)</div>
                  <div className="mt-1 text-lg font-semibold text-indigo-700">{fmtUF(summary?.primaTotalUF ?? 0)}</div>
                  <div className="text-sm text-muted-foreground">{fmtCLP(summary?.primaTotalCLP ?? 0)}</div>
                </div>
              </div>
            ) : mode === 'arr' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">ARR (12 × MRR)</div>
                  <div className="mt-1 text-lg font-semibold text-indigo-700">{fmtUF(summary?.arrUF ?? 0)}</div>
                  {hasRate && <div className="text-sm text-muted-foreground">{fmtCLP(summary?.arrCLP ?? 0)}</div>}
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">MRR base</div>
                  <div className="mt-1 text-lg font-semibold text-green-700">{fmtUF(summary?.mrrUF ?? 0)}</div>
                  {hasRate && <div className="text-sm text-muted-foreground">{fmtCLP(summary?.mrrCLP ?? 0)}</div>}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white/50 rounded-lg">
                    <div className="text-xs text-muted-foreground">Prima Total (recaudada)</div>
                    <div className="mt-1 text-lg font-semibold text-indigo-700">{fmtUF(summary?.primaTotalUF ?? 0)}</div>
                    <div className="text-sm text-muted-foreground">{fmtCLP(summary?.primaTotalCLP ?? 0)}</div>
                  </div>
                  <div className="text-center p-4 bg-white/50 rounded-lg">
                    <div className="text-xs text-muted-foreground">MRR (suscripciones vigentes)</div>
                    <div className="mt-1 text-lg font-semibold text-green-700">{fmtUF(summary?.mrrUF ?? 0)}</div>
                    {hasRate && <div className="text-sm text-muted-foreground">{fmtCLP(summary?.mrrCLP ?? 0)}</div>}
                  </div>
                </div>
                <div className="text-center p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg">
                  <div className="text-sm opacity-90">ARR (charges = 12 × MRR)</div>
                  <div className="text-2xl font-bold mt-1">{fmtUF(summary?.arrUF ?? 0)}</div>
                  {hasRate && <div className="text-sm opacity-90">{fmtCLP(summary?.arrCLP ?? 0)}</div>}
                  <div className="text-xs opacity-75 mt-1">Suscription charges suman el ARR</div>
                </div>
              </>
            )}

            <div className="text-xs text-muted-foreground text-center">
              <strong>Cálculo:</strong> Prima recaudada (UF→CLP por fecha), MRR por mes de cobertura, ARR = MRR × 12
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Por compañía */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-indigo-600" />
            {mode === 'mrr' ? 'MRR por Compañía' : mode === 'arr' ? 'ARR por Compañía (12 × MRR)' : 'Prima por Compañía'}
            {options.companyName ? `· Filtro: ${options.companyName}` : options.companyId ? `· Filtro ID: ${options.companyId}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {porCompania.length === 0 ? (
            <div className="text-center p-4 text-muted-foreground">Sin datos para los filtros seleccionados.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {porCompania.map((c) => (
                <div key={`${c.compania_id || c.compania_nombre || 'sin'}`} className="p-4 bg-white/50 rounded-lg">
                  <div className="text-sm font-semibold">{c.compania_nombre || c.compania_id || 'Sin compañía'}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {mode === 'mrr' ? (
                      <>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">MRR</div>
                          <div className="text-sm font-medium text-green-700">{fmtUF(c?.mrrUF ?? 0)}</div>
                          <div className="text-xs">{fmtCLP(c?.mrrCLP ?? 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">Recaudada</div>
                          <div className="text-sm font-medium text-indigo-700">{fmtUF(c?.primaRecaudadaUF ?? 0)}</div>
                          <div className="text-xs text-muted-foreground">{fmtCLP(c?.primaRecaudadaCLP ?? 0)}</div>
                        </div>
                      </>
                    ) : mode === 'arr' ? (
                      <>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">ARR</div>
                          <div className="text-sm font-medium text-indigo-700">{fmtUF(c?.arrUF ?? 0)}</div>
                          <div className="text-xs text-muted-foreground">{fmtCLP(c?.arrCLP ?? 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">MRR base</div>
                          <div className="text-sm font-medium text-green-700">{fmtUF(c?.mrrUF ?? 0)}</div>
                          <div className="text-xs">{fmtCLP(c?.mrrCLP ?? 0)}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">Recaudada</div>
                          <div className="text-sm font-medium text-indigo-700">{fmtUF(c?.primaRecaudadaUF ?? 0)}</div>
                          <div className="text-xs text-muted-foreground">{fmtCLP(c?.primaRecaudadaCLP ?? 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">MRR · ARR</div>
                          <div className="text-sm font-medium text-green-700">{fmtUF(c?.mrrUF ?? 0)}</div>
                          <div className="text-xs">{fmtCLP(c?.mrrCLP ?? 0)}</div>
                          <div className="text-xs text-muted-foreground mt-1">ARR: {fmtUF(c?.arrUF ?? 0)} · {fmtCLP(c?.arrCLP ?? 0)}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notas */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            Notas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
            <li>La prima es en UF; al recaudar se convierte a CLP según fecha.</li>
            <li>MRR incluye suscripciones con cobertura vigente en el período.</li>
            <li>ARR se calcula como 12 × MRR; los charges lo suman.</li>
            <li>Puedes filtrar por compañía con `companyId` o `companyName`.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default CertificatesMetricsCards;